import { memo, useState } from 'react';
import { roundBook, fmtBook } from '../../utils/casinoBook';
import Collapse from '../Collapse';

const CDN = 'https://wver.sprintstaticdata.com/v230/static/front/img/cards';

function findBySid(sub, sid) {
  return sub.find((s) => s.sid === sid || s.sid === String(sid));
}
function isSusp(item) {
  return !item || item.gstatus === 'SUSPENDED' || item.gstatus === '0';
}
function suspCls(item) {
  return isSusp(item) ? ' suspended' : '';
}
function fmtAmt(n) {
  const v = Number(n) || 0;
  if (v >= 100000) return `${v / 100000}L`;
  if (v >= 1000) return `${v / 1000}K`;
  return String(v);
}
function bookOf(exposures, nat) {
  return roundBook(exposures?.[nat] ?? 0);
}
function bookCls(v) {
  return v < 0 ? 'book-red' : v > 0 ? 'book-green' : 'book-black';
}

// bare info-circle + collapse range (id must be unique per instance)
function Rng({ id, min, max }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <i
        data-toggle="collapse"
        data-target={`#${id}`}
        className="fas fa-info-circle float-right"
        onClick={() => setOpen((v) => !v)}
      ></i>
      {' '}
      <Collapse id={id} open={open} className="icon-range">
        {'\n                          R:'}
        <span>{fmtAmt(min)}</span>
        -
        <span>{fmtAmt(max)}</span>
      </Collapse>
    </>
  );
}

// per-market range limits (min 100 for all)
const LIMITS = {
  player: [100, 200000],
  bacc: [100, 50000],
  total: [100, 25000],
  pair: [100, 10000],
  suit: [100, 10000],
};

// 20-20 Teenpatti B — table teenpatti20. Player A/B sides, each: Player /
// 3 Baccarat / Total / Pair Plus back rows + Black(spade,club) / Red(heart,
// diamond) suit boxes. Web = teen20left/center/right (d-none-small), mobile =
// 3-col label|A|B stack (d-none-big). book-black hamesha. Suits CDN se.
const BetTableTeen20b = memo(function BetTableTeen20b({ gameData, onBet, exposures }) {
  const sub = gameData?.sub || [];
  const handleBet = onBet || (() => {});

  const playerA = findBySid(sub, 1);
  const playerB = findBySid(sub, 2);
  const pairA   = findBySid(sub, 3);
  const pairB   = findBySid(sub, 4);
  const baccA   = findBySid(sub, 5);
  const baccB   = findBySid(sub, 6);
  const blackA  = findBySid(sub, 7);
  const redA    = findBySid(sub, 8);
  const blackB  = findBySid(sub, 9);
  const redB    = findBySid(sub, 10);
  const totalA  = findBySid(sub, 11);
  const totalB  = findBySid(sub, 12);

  // With a book, a locked cell pins its padlock to the TOP (lock-top) and the
  // book is lifted above the overlay (index.css) — same as the player site.
  // web back box (odd + casino-book book-black)
  const webBox = (item, odd) => {
    const v = bookOf(exposures, item?.nat);
    return (
      <div
        className={`back casino-bl-box-item${v ? ' lock-top' : ''}${suspCls(item)}`}
        onClick={isSusp(item) ? undefined : () => handleBet(item.sid, 'back', item.b, item)}
      >
        <span className="casino-box-odd">{odd ?? (item?.b ?? 0)}</span>
        {' '}
        <span className={`casino-book ${bookCls(v)}`}>{fmtBook(v)}</span>
      </div>
    );
  };

  // mobile back box (odd + book-black)
  const mobBox = (item, odd) => {
    const v = bookOf(exposures, item?.nat);
    return (
      <div
        className={`back casino-bl-box-item${v ? ' lock-top' : ''}${suspCls(item)}`}
        onClick={isSusp(item) ? undefined : () => handleBet(item.sid, 'back', item.b, item)}
      >
        <span className="casino-box-odd">{odd ?? (item?.b ?? 0)}</span>
        {' '}
        <span className={bookCls(v)}>{fmtBook(v)}</span>
      </div>
    );
  };

  // web suit box (black/red) — img wrapper + odd/book, then range
  const webSuit = (item, kind, rid, isA = true) => {
    const v = bookOf(exposures, item?.nat);
    const isBlack = kind === 'black';
    return (
      <div className={`casino-rb-box ${isBlack ? 'blackcontainer' : 'redcontainer'}`}>
        <div
          className={`casino-rb-box-player ${isBlack ? 'blackbox' : 'redbox'} back${suspCls(item)}`}
          onClick={isSusp(item) ? undefined : () => handleBet(item.sid, 'back', item.b, item)}
        >
          {isBlack ? (
            // Reference: only Player A's black box right-aligns its suit icons.
            <div className={isA ? 'text-right' : undefined}>
              <img src={`${CDN}/spade.png`} />
              {' '}
              <img src={`${CDN}/club.png`} />
            </div>
          ) : (
            <div>
              <img src={`${CDN}/heart.png`} />
              {' '}
              <img src={`${CDN}/diamond.png`} className="diamond-icon" />
            </div>
          )}
          <div className="text-right">
            <span className="d-block casino-box-odd">{item?.b ?? 0}</span>
            {' '}
            <span className={`casino-book ${bookCls(v)}`}>{fmtBook(v)}</span>
          </div>
        </div>
        {' '}
        <div className="text-right casino-rb-box-player-range">
          <Rng id={rid} min={LIMITS.suit[0]} max={LIMITS.suit[1]} />
        </div>
      </div>
    );
  };

  // web side (left/right)
  const webSide = (side) => {
    const isA = side === 'a';
    const items = isA
      ? { player: playerA, bacc: baccA, total: totalA, pair: pairA, black: blackA, red: redA }
      : { player: playerB, bacc: baccB, total: totalB, pair: pairB, black: blackB, red: redB };
    const rid = isA ? [1, 2, 3, 4, 5, 6] : [7, 8, 9, 10, 11, 12];
    return (
      <div className={isA ? 'teen20left' : 'teen20right'}>
        <div className="casino-box-row">
          <div className="casino-nation-name no-border casino-bl-box-title">
            <div className={isA ? 'playera' : 'playerb'}>{isA ? 'Player A' : 'Player B'}</div>
          </div>
        </div>
        {' '}
        <div className="casino-bl-box casino-bl-box-title">
          <div className="casino-bl-box-item">
            <span>Player</span>
            {' '}
            <div className="ml-1"><Rng id={`range${rid[0]}`} min={LIMITS.player[0]} max={LIMITS.player[1]} /></div>
          </div>
          <div className="casino-bl-box-item">
            <span>3 Baccarat</span>
            {' '}
            <div className="ml-1"><Rng id={`range${rid[1]}`} min={LIMITS.bacc[0]} max={LIMITS.bacc[1]} /></div>
          </div>
          <div className="casino-bl-box-item">
            <span>Total</span>
            {' '}
            <div className="ml-1"><Rng id={`range${rid[2]}`} min={LIMITS.total[0]} max={LIMITS.total[1]} /></div>
          </div>
          <div className="casino-bl-box-item">
            <span>Pair Plus</span>
            {' '}
            <div className="ml-1"><Rng id={`range${rid[3]}`} min={LIMITS.pair[0]} max={LIMITS.pair[1]} /></div>
          </div>
        </div>
        {' '}
        <div className="casino-bl-box mb-4">
          {webBox(items.player)}
          {webBox(items.bacc)}
          {webBox(items.total)}
          {webBox(items.pair, isA ? 'A' : 'B')}
        </div>
        {' '}
        <div className="casino-rb-box-container mb-3">
          {webSuit(items.black, 'black', `range${rid[4]}`, isA)}
          {' '}
          {webSuit(items.red, 'red', `range${rid[5]}`)}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Desktop */}
      <div className="teen20casino-container d-none-small">
        {webSide('a')}
        {' '}
        <div className="teen20center"></div>
        {' '}
        {webSide('b')}
      </div>

      {/* Mobile */}
      <div className="teen20casino-container d-none-big">
        <div className="casino-bl-box casino-bl-box-title">
          <div className="casino-bl-box-item"></div>
          <div className="casino-bl-box-item playera">Player A</div>
          <div className="casino-bl-box-item playerb">Player B</div>
        </div>
        {' '}
        <div className="casino-bl-box">
          <div className="casino-bl-box-item casino-odds-name casino-nation-name">
            <span>Player</span>
            {' '}
            <Rng id="mrange1" min={LIMITS.player[0]} max={LIMITS.player[1]} />
          </div>
          {' '}
          {mobBox(playerA)}
          {mobBox(playerB)}
        </div>
        {' '}
        <div className="casino-bl-box">
          <div className="casino-bl-box-item casino-odds-name casino-nation-name">
            <span>3 Baccarat</span>
            {' '}
            <Rng id="mrange2" min={LIMITS.bacc[0]} max={LIMITS.bacc[1]} />
          </div>
          {' '}
          {mobBox(baccA)}
          {mobBox(baccB)}
        </div>
        {' '}
        <div className="casino-bl-box">
          <div className="casino-bl-box-item casino-odds-name casino-nation-name">
            <span>Total</span>
            {' '}
            <Rng id="mrange3" min={LIMITS.total[0]} max={LIMITS.total[1]} />
          </div>
          {' '}
          {mobBox(totalA)}
          {mobBox(totalB)}
        </div>
        {' '}
        <div className="casino-bl-box">
          <div className="casino-bl-box-item casino-odds-name casino-nation-name">
            <span>Pair Plus</span>
            {' '}
            <Rng id="mrange4" min={LIMITS.pair[0]} max={LIMITS.pair[1]} />
          </div>
          {' '}
          {mobBox(pairA, 'A')}
          {mobBox(pairB, 'B')}
        </div>
        {' '}
        <div className="casino-bl-box">
          <div className="casino-bl-box-item casino-odds-name casino-nation-name">
            <div>
              <img src={`${CDN}/spade.png`} />
              {' '}
              <img src={`${CDN}/club.png`} />
            </div>
            {' '}
            <Rng id="mrange5" min={LIMITS.suit[0]} max={LIMITS.suit[1]} />
          </div>
          {' '}
          {mobBox(blackA)}
          {mobBox(blackB)}
        </div>
        {' '}
        <div className="casino-bl-box">
          <div className="casino-bl-box-item casino-odds-name casino-nation-name">
            <div>
              <img src={`${CDN}/heart.png`} />
              {' '}
              <img src={`${CDN}/diamond.png`} />
            </div>
            {' '}
            <Rng id="mrange6" min={LIMITS.suit[0]} max={LIMITS.suit[1]} />
          </div>
          {' '}
          {mobBox(redA)}
          {mobBox(redB)}
        </div>
      </div>
    </>
  );
});

export default BetTableTeen20b;
